import { h, Fragment, shallowRef, triggerRef } from 'vue';
import { createVNodeFromHtml } from './TemplateRenderer.js';

// Single global cache with frozen VNodes
const frozenVNodeCache = new Map();

/**
 * Clear the cache
 */
export const clearStableSectionCache = () => {
  frozenVNodeCache.clear();
  console.log('🧹 Cache cleared');
};

/**
 * Generate hash for content
 */
const hash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(31, h) + str.charCodeAt(i) | 0;
  }
  return h.toString(36);
};

/**
 * Create a completely frozen VNode that Vue won't touch
 */
const createFrozenVNode = (content, context, componentInstance, key) => {
  if (frozenVNodeCache.has(key)) {
    console.log('❄️ Using frozen VNode:', key);
    return frozenVNodeCache.get(key);
  }

  console.log('🧊 Creating frozen VNode:', key);
  
  // Create the VNode normally first
  const vnode = createVNodeFromHtml(content, context, componentInstance);
  
  // Freeze the VNode to prevent Vue from modifying it
  if (vnode) {
    Object.freeze(vnode);
    if (vnode.props) Object.freeze(vnode.props);
    if (vnode.children) Object.freeze(vnode.children);
  }
  
  frozenVNodeCache.set(key, vnode);
  return vnode;
};

/**
 * Simple auto-marking - just mark anything with static images
 */
export const autoMarkStableSections = (html) => {
  if (!html) return html;
  
  // Mark any container with a static image URL
  return html.replace(
    /<(li|div|span|section)([^>]*?)>([\s\S]*?<img[^>]*src="https?:\/\/[^"']*"[^>]*>[\s\S]*?)<\/\1>/gi,
    (match, tag, attrs, content) => {
      // Skip if already marked or if image src contains template variables
      if (attrs.includes('data-stable') || /<img[^>]*src[^>]*\{\{/.test(content)) {
        return match;
      }
      
      const id = `stable-${hash(content)}`;
      const newAttrs = attrs.trim() ? `${attrs} data-stable="${id}"` : `data-stable="${id}"`;
      return `<${tag} ${newAttrs}>${content}</${tag}>`;
    }
  );
};

/**
 * Split HTML into stable and dynamic sections
 */
const splitIntoSections = (html) => {
  const marked = autoMarkStableSections(html);
  const sections = [];
  let lastIndex = 0;
  
  const regex = /<([^>]+)\s+data-stable="([^"]+)"[^>]*>/g;
  let match;
  
  while ((match = regex.exec(marked)) !== null) {
    // Add dynamic content before this stable section
    const before = marked.substring(lastIndex, match.index).trim();
    if (before) {
      sections.push({ type: 'dynamic', content: before });
    }
    
    // Find the complete stable section
    const tag = match[1].split(' ')[0];
    const id = match[2];
    const start = match.index;
    
    const fullSection = extractTag(marked, start, tag);
    if (fullSection) {
      sections.push({ 
        type: 'stable', 
        content: fullSection,
        id: id,
        key: hash(fullSection)
      });
      lastIndex = start + fullSection.length;
    }
  }
  
  // Add remaining dynamic content
  const remaining = marked.substring(lastIndex).trim();
  if (remaining) {
    sections.push({ type: 'dynamic', content: remaining });
  }
  
  return sections;
};

/**
 * Extract a complete HTML tag with proper nesting
 */
const extractTag = (html, startIndex, tagName) => {
  let depth = 0;
  let i = startIndex;
  
  // Find end of opening tag
  while (i < html.length && html[i] !== '>') i++;
  i++;
  
  depth = 1;
  const openTag = new RegExp(`<${tagName}(?:\\s|>)`, 'i');
  const closeTag = new RegExp(`</${tagName}>`, 'i');
  
  while (i < html.length && depth > 0) {
    const rest = html.substring(i);
    const openMatch = rest.match(openTag);
    const closeMatch = rest.match(closeTag);
    
    if (!closeMatch) break;
    
    const openPos = openMatch?.index ?? Infinity;
    const closePos = closeMatch.index;
    
    if (openPos < closePos) {
      depth++;
      i += openPos + openMatch[0].length;
    } else {
      depth--;
      i += closePos + closeMatch[0].length;
    }
  }
  
  return depth === 0 ? html.substring(startIndex, i) : null;
};

/**
 * Main render function - the simple approach
 */
export const renderTemplateWithStableSections = (html, context, componentInstance) => {
  const sections = splitIntoSections(html);
  
  if (sections.length === 1 && sections[0].type === 'dynamic') {
    // No stable sections found, render normally
    return createVNodeFromHtml(html, context, componentInstance);
  }
  
  console.log(`🔧 Rendering ${sections.length} sections`);
  
  const vnodes = sections.map((section, index) => {
    if (section.type === 'stable') {
      // Use frozen VNode for stable sections
      return createFrozenVNode(section.content, context, componentInstance, section.key);
    } else {
      // Render dynamic sections normally each time
      return createVNodeFromHtml(section.content, context, componentInstance);
    }
  });
  
  const valid = vnodes.filter(v => v != null);
  
  return valid.length === 1 ? valid[0] : h(Fragment, {}, valid);
};

/**
 * Force refresh a section
 */
export const invalidateStableSection = (sectionId) => {
  let found = false;
  for (const [key, vnode] of frozenVNodeCache.entries()) {
    if (key.includes(sectionId)) {
      frozenVNodeCache.delete(key);
      found = true;
    }
  }
  if (found) console.log('🗑️ Invalidated:', sectionId);
  return found;
};

/**
 * Debug info
 */
export const getCacheStats = () => ({
  frozenVNodes: frozenVNodeCache.size
});